import type { JsonObject } from "@bufbuild/protobuf";
import { format } from "date-fns";
import { type Getter, type Setter, atom } from "jotai";
import { normalize } from "pathe";

import {
	type DocumentMetadata,
	DocumentMetadataSchema,
	generateShortUuid,
	serializePage,
} from "@moment/api-collab";
import type { HttpRequestPage } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import {
	$addDraftDocument,
	$document,
	type DocsListItem,
	docsListCollabEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $loadDocumentMetadataFromDisk } from "../document-metadata/load-metadata-from-disk.atom";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { $git } from "../git";
import { buildDefaultPagePath } from "../page-metadata/page-metadata.atom";
import { $pageCanvasSnapshot, canvasSnapshotKeyForDocsRoute } from "../page/page-canvas-state.atom";
import { paths } from "../paths";
import { $editorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { $mdPageCollabConnection } from "../reboot/collab-connection.atom";
import { $metadataCollabConnection } from "../reboot/metadata-collab-connection.atom";
import { $requestPageCollabConnection } from "../reboot/request-page-collab-connection.atom";
import { getLatestMetadata, getLatestRequestPage } from "../reboot/utils";
import { $httpRequestPageReader } from "../request-page/http-request-page-reader";
import { requestPageKeyForDocsRoute } from "../request-page/requestPageKey";
import { AGENTS_MD_TEMPLATE } from "./agents-template";
import { $setTemplateOnboarding } from "./template-onboarding.atom";
import { TemplateOnboardingConfigSchema } from "./template-onboarding.types";

const logger = new Logger("duplicateDocument");

const INTERNAL_REPO_DIR_NAMES = new Set([".git", ".jj"]);

export class DuplicateDocumentNotFoundError extends Error {
	public readonly tag = "duplicate-document-not-found-error" as const;

	constructor(documentId: string) {
		super(`Cannot duplicate document ${documentId}: document was not found`);
		this.name = "DuplicateDocumentNotFoundError";
	}
}

export class DuplicateDocumentNoSourceError extends Error {
	public readonly tag = "duplicate-document-no-source-error" as const;

	constructor(documentId: string) {
		super(
			`Cannot duplicate document ${documentId}: no local files or remote document source are available`
		);
		this.name = "DuplicateDocumentNoSourceError";
	}
}

export class DuplicateDocumentCanvasSnapshotUnavailableError extends Error {
	public readonly tag = "duplicate-document-canvas-snapshot-unavailable-error" as const;

	constructor(documentId: string, pageId: string) {
		super(
			`Cannot duplicate document ${documentId}: canvas page ${pageId} is not loaded locally`
		);
		this.name = "DuplicateDocumentCanvasSnapshotUnavailableError";
	}
}

const buildDuplicateTitle = (title: string): string => {
	const trimmedTitle = title.trim();
	return trimmedTitle ? `${trimmedTitle} copy` : "Untitled copy";
};

const buildRelativePath = (parentPath: string, childName: string): string =>
	normalize(parentPath ? `${parentPath}/${childName}` : childName);

const utf8Decoder = new TextDecoder("utf-8", { fatal: true });
const utf8Encoder = new TextEncoder();

const decodeUtf8IfPossible = (contents: Uint8Array | undefined): string | undefined => {
	if (!contents) {
		return undefined;
	}

	try {
		return utf8Decoder.decode(contents);
	} catch {
		return undefined;
	}
};

const replaceDocumentIdInString = (
	value: string,
	sourceDocumentId: string,
	duplicatedDocumentId: string
): string => value.split(sourceDocumentId).join(duplicatedDocumentId);

// Duplication is expected to rewrite visible document-id references across arbitrary user
// content, so this intentionally does a blind recursive string replacement instead of trying
// to parse every possible payload type. That can replace incidental substring matches too.
const replaceDocumentIdInValue = (
	value: unknown,
	sourceDocumentId: string,
	duplicatedDocumentId: string
): unknown => {
	if (typeof value === "string") {
		return replaceDocumentIdInString(value, sourceDocumentId, duplicatedDocumentId);
	}

	if (value instanceof Date || value === null || value === undefined) {
		return value;
	}

	if (Array.isArray(value)) {
		return value.map((item) =>
			replaceDocumentIdInValue(item, sourceDocumentId, duplicatedDocumentId)
		);
	}

	if (typeof value === "object") {
		return Object.fromEntries(
			Object.entries(value).map(([key, itemValue]) => [
				replaceDocumentIdInString(key, sourceDocumentId, duplicatedDocumentId),
				replaceDocumentIdInValue(itemValue, sourceDocumentId, duplicatedDocumentId),
			])
		);
	}

	return value;
};

const buildDuplicatedMetadata = ({
	sourceMetadata,
	sourceDocumentId,
	duplicatedDocumentId,
	title,
}: {
	sourceMetadata: DocumentMetadata;
	sourceDocumentId: string;
	duplicatedDocumentId: string;
	title?: string;
}): DocumentMetadata =>
	DocumentMetadataSchema.parse({
		...(replaceDocumentIdInValue(
			sourceMetadata,
			sourceDocumentId,
			duplicatedDocumentId
		) as DocumentMetadata),
		id: duplicatedDocumentId,
		title:
			title ??
			buildDuplicateTitle(
				replaceDocumentIdInString(
					sourceMetadata.title,
					sourceDocumentId,
					duplicatedDocumentId
				)
			),
		created: new Date(),
		updated: new Date(),
	});

const buildDuplicatedDocsListItem = ({
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
	duplicatedMetadata,
}: {
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
	duplicatedMetadata: DocumentMetadata;
}): DocsListItem => ({
	id: duplicatedDocumentId,
	local: {
		[duplicatedDocumentDirPath]: {
			path: duplicatedDocumentDirPath,
			title: duplicatedMetadata.title,
			createdAt: duplicatedMetadata.created,
			updatedAt: duplicatedMetadata.updated,
			type: duplicatedMetadata.type,
		},
	},
	remote: {},
});

const getAccessTokenIfAvailable = async (get: Getter): Promise<string | undefined> => {
	const { getAccessTokenSilently } = get($accessTokenFunc);
	return getAccessTokenSilently ? await getAccessTokenSilently() : undefined;
};

const getDuplicateCommitMessage = (sourceTitle: string): string => {
	const prettyTimestamp = format(new Date(), "PPppp");
	return `🚀 Duplicated from "${sourceTitle.trim() || "Untitled"}" at ${prettyTimestamp}`;
};

const sanitizeGitConfig = (gitConfig: string): string => {
	const lines = gitConfig.split(/\r?\n/);
	const sanitizedLines: string[] = [];
	let currentHeader: string | null = null;
	let currentSectionLines: string[] = [];

	const flushSection = () => {
		if (currentHeader === null) {
			sanitizedLines.push(...currentSectionLines);
			currentSectionLines = [];
			return;
		}

		const normalizedHeader = currentHeader.trim().toLowerCase();
		if (normalizedHeader.startsWith("[remote ")) {
			currentHeader = null;
			currentSectionLines = [];
			return;
		}

		let sectionLines = currentSectionLines;
		if (normalizedHeader.startsWith("[branch ")) {
			sectionLines = currentSectionLines.filter(
				(line) => !/^\s*(remote|pushremote|merge)\s*=/i.test(line)
			);
		}

		const hasMeaningfulLines = sectionLines.some((line) => {
			const trimmed = line.trim();
			return trimmed !== "" && !trimmed.startsWith("#") && !trimmed.startsWith(";");
		});

		if (hasMeaningfulLines) {
			sanitizedLines.push(currentHeader, ...sectionLines);
		}

		currentHeader = null;
		currentSectionLines = [];
	};

	for (const line of lines) {
		if (/^\s*\[.*\]\s*$/.test(line)) {
			flushSection();
			currentHeader = line;
			continue;
		}

		currentSectionLines.push(line);
	}

	flushSection();

	const sanitizedConfig = sanitizedLines.join("\n");
	return gitConfig.endsWith("\n") ? `${sanitizedConfig}\n` : sanitizedConfig;
};

const copyDirectoryTree = async ({
	get,
	set,
	sourceDirPath,
	destinationDirPath,
	relativeDirPath = "",
}: {
	get: Getter;
	set: Setter;
	sourceDirPath: string;
	destinationDirPath: string;
	relativeDirPath?: string;
}): Promise<void> => {
	const fsApi = fs();

	await set(fsApi.mkdir, { dirPath: destinationDirPath });

	const entries = await get(fsApi.readDir({ dirPath: sourceDirPath }));
	for (const entry of entries) {
		if (!entry.name) {
			continue;
		}

		const relativePath = buildRelativePath(relativeDirPath, entry.name);
		const sourceEntryPath = await get(fsApi.pathJoin([sourceDirPath, entry.name]));
		const destinationEntryPath = await get(fsApi.pathJoin([destinationDirPath, entry.name]));

		if (entry.isSymlink) {
			logger.debug("Skipping symlink while duplicating document", {
				path: sourceEntryPath,
			});
			continue;
		}

		if (entry.isDirectory) {
			await copyDirectoryTree({
				get,
				set,
				sourceDirPath: sourceEntryPath,
				destinationDirPath: destinationEntryPath,
				relativeDirPath: relativePath,
			});
			continue;
		}

		if (!entry.isFile) {
			continue;
		}

		const contents = await get(fsApi.readBinaryFile({ filePath: sourceEntryPath }));
		await set(fsApi.writeBinaryFile, {
			filePath: destinationEntryPath,
			content: contents,
		});
	}
};

const replaceDocumentIdInTextFiles = async ({
	get,
	set,
	rootDirPath,
	sourceDocumentId,
	duplicatedDocumentId,
	skipDirNames = new Set<string>(),
	relativeDirPath = "",
}: {
	get: Getter;
	set: Setter;
	rootDirPath: string;
	sourceDocumentId: string;
	duplicatedDocumentId: string;
	skipDirNames?: Set<string>;
	relativeDirPath?: string;
}): Promise<void> => {
	const fsApi = fs();
	const currentDirPath = relativeDirPath
		? await get(fsApi.pathJoin([rootDirPath, relativeDirPath]))
		: rootDirPath;
	const entries = await get(fsApi.readDir({ dirPath: currentDirPath }));

	for (const entry of entries) {
		if (!entry.name || entry.isSymlink) {
			continue;
		}

		const entryRelativePath = buildRelativePath(relativeDirPath, entry.name);
		const entryPath = await get(fsApi.pathJoin([rootDirPath, entryRelativePath]));

		if (entry.isDirectory) {
			if (!relativeDirPath && skipDirNames.has(entry.name)) {
				continue;
			}

			await replaceDocumentIdInTextFiles({
				get,
				set,
				rootDirPath,
				sourceDocumentId,
				duplicatedDocumentId,
				skipDirNames,
				relativeDirPath: entryRelativePath,
			});
			continue;
		}

		if (!entry.isFile) {
			continue;
		}

		const contents = await get(fsApi.readBinaryFile({ filePath: entryPath }));
		const text = decodeUtf8IfPossible(contents);
		if (!text || !text.includes(sourceDocumentId)) {
			continue;
		}

		const replacedText = text.split(sourceDocumentId).join(duplicatedDocumentId);
		if (replacedText === text) {
			continue;
		}

		await set(fsApi.writeBinaryFile, {
			filePath: entryPath,
			content: utf8Encoder.encode(replacedText),
		});
	}
};

const removeRemoteEntriesFromGitConfig = async ({
	get,
	set,
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
}: {
	get: Getter;
	set: Setter;
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
}): Promise<void> => {
	const fsApi = fs();
	const gitConfigPath = await get(fsApi.pathJoin([duplicatedDocumentDirPath, ".git", "config"]));
	const gitConfigExists = await get(fsApi.exists({ filePath: gitConfigPath }));
	if (!gitConfigExists) {
		return;
	}

	const gitConfig = await get(fsApi.readTextFile({ filePath: gitConfigPath }));
	const sanitizedGitConfig = sanitizeGitConfig(gitConfig);
	if (sanitizedGitConfig === gitConfig) {
		return;
	}

	await set(fsApi.writeTextFile, {
		filePath: gitConfigPath,
		content: sanitizedGitConfig,
		documentId: duplicatedDocumentId,
	});
};

const cleanupDuplicatedPaths = async ({
	set,
	documentDirPath,
	documentEnvironmentsDirPath,
}: {
	set: Setter;
	documentDirPath: string;
	documentEnvironmentsDirPath: string;
}): Promise<void> => {
	const fsApi = fs();

	for (const filePath of [documentDirPath, documentEnvironmentsDirPath]) {
		try {
			await set(fsApi.remove, { filePath });
		} catch (cleanupError) {
			logger.warn("Failed to clean up duplicated document path", {
				filePath,
				cleanupError,
			});
		}
	}
};

const duplicateDocumentEnvironments = async ({
	get,
	set,
	sourceDocumentId,
	duplicatedDocumentId,
}: {
	get: Getter;
	set: Setter;
	sourceDocumentId: string;
	duplicatedDocumentId: string;
}): Promise<void> => {
	const fsApi = fs();
	const pathsApi = paths();

	const sourceEnvironmentsDirPath = await get(
		pathsApi.getDocumentEnvironmentsDirPath(sourceDocumentId)
	);
	const sourceEnvironmentDirExists = await get(
		fsApi.exists({ filePath: sourceEnvironmentsDirPath })
	);
	if (!sourceEnvironmentDirExists) {
		return;
	}

	const duplicatedEnvironmentsDirPath = await get(
		pathsApi.getDocumentEnvironmentsDirPath(duplicatedDocumentId)
	);
	await copyDirectoryTree({
		get,
		set,
		sourceDirPath: sourceEnvironmentsDirPath,
		destinationDirPath: duplicatedEnvironmentsDirPath,
	});
};

const loadTemplateOnboardingIfPresent = async ({
	get,
	set,
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
}: {
	get: Getter;
	set: Setter;
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
}): Promise<void> => {
	const fsApi = fs();
	const templateConfigPath = await get(
		fsApi.pathJoin([duplicatedDocumentDirPath, ".moment", "template.yml"])
	);
	const templateConfigExists = await get(fsApi.exists({ filePath: templateConfigPath }));
	if (!templateConfigExists) {
		return;
	}

	const parsed = await get(fsApi.readYamlFile({ filePath: templateConfigPath }));
	const result = TemplateOnboardingConfigSchema.safeParse(parsed);
	if (!result.success) {
		logger.warn("Skipping invalid duplicated template onboarding config", {
			duplicatedDocumentId,
			templateConfigPath,
			error: result.error,
		});
		return;
	}

	await set($setTemplateOnboarding, {
		documentId: duplicatedDocumentId,
		config: result.data,
	});
};

type DuplicateDocumentSnapshot = {
	metadata: DocumentMetadata;
	markdownPages: Record<string, string>;
	requestPages: Record<string, HttpRequestPage>;
	canvasSnapshots: Record<string, unknown>;
};

type DocumentPageMetadata = NonNullable<DocumentMetadata["pagesMetadata"]>[number];

const buildDuplicatedPagePairs = ({
	sourceMetadata,
	duplicatedMetadata,
}: {
	sourceMetadata: DocumentMetadata;
	duplicatedMetadata: DocumentMetadata;
}): Array<{ sourcePage: DocumentPageMetadata; duplicatedPage: DocumentPageMetadata }> => {
	const sourcePages = sourceMetadata.pagesMetadata ?? [];
	const duplicatedPages = duplicatedMetadata.pagesMetadata ?? [];

	if (sourcePages.length !== duplicatedPages.length) {
		throw new Error("Duplicate document metadata page count changed unexpectedly");
	}

	return duplicatedPages.map((duplicatedPage, index) => {
		const sourcePage = sourcePages[index];
		if (!sourcePage) {
			throw new Error("Missing source page while writing duplicated document");
		}

		return { sourcePage, duplicatedPage };
	});
};

const getSourceDocumentMetadataForDuplication = async ({
	get,
	sourceDocument,
	sourceDocumentId,
}: {
	get: Getter;
	sourceDocument: DocsListItem;
	sourceDocumentId: string;
}): Promise<DocumentMetadata> => {
	const loadedMetadata = get($documentMetadata(metadataKeyForDocsRoute(sourceDocumentId)));
	if (loadedMetadata) {
		return loadedMetadata;
	}

	const remoteEntry = docsListItemRemoteEntry(sourceDocument);
	if (!remoteEntry) {
		throw new DuplicateDocumentNoSourceError(sourceDocumentId);
	}

	const collabEntry = docsListCollabEntry(remoteEntry);
	if (collabEntry) {
		try {
			const connection = await get(
				$metadataCollabConnection({
					workspaceSlug: remoteEntry.workspace.slug,
					documentId: sourceDocumentId,
					branch: collabEntry.branch,
					sha: collabEntry.sha,
				})
			);
			const metadata = connection ? getLatestMetadata(connection.store) : null;
			if (metadata) {
				return metadata;
			}
		} catch (error) {
			logger.warn("Falling back to DocsAPI for duplicate metadata fetch", {
				sourceDocumentId,
				error,
			});
		}
	}

	const token = await getAccessTokenIfAvailable(get);
	const metadata = await get($docsApi).document.metadata.read(
		{
			workspaceName: remoteEntry.workspace.name,
			documentId: sourceDocumentId,
		},
		token
	);

	if (!metadata) {
		throw new Error(
			`No metadata returned while duplicating remote document ${sourceDocumentId}`
		);
	}

	return metadata;
};

const getMarkdownPageForDuplication = async ({
	get,
	sourceDocument,
	sourceDocumentId,
	sourcePages,
	page,
}: {
	get: Getter;
	sourceDocument: DocsListItem;
	sourceDocumentId: string;
	sourcePages: DocumentPageMetadata[];
	page: DocumentPageMetadata;
}): Promise<string> => {
	const { pagePath, pageId } = {
		pageId: page.id,
		pagePath: page.path ?? buildDefaultPagePath(sourcePages, page),
	};
	const loadedEditorState = get(
		$editorState(editorStateKeyForDocsRoute({ documentId: sourceDocumentId, pageId }))
	);
	if (loadedEditorState) {
		return serializePage(loadedEditorState.doc);
	}

	const remoteEntry = docsListItemRemoteEntry(sourceDocument);
	if (!remoteEntry) {
		throw new DuplicateDocumentNoSourceError(sourceDocumentId);
	}

	const collabEntry = docsListCollabEntry(remoteEntry);
	if (collabEntry) {
		try {
			const connection = get(
				$mdPageCollabConnection({
					workspaceSlug: remoteEntry.workspace.slug,
					documentId: sourceDocumentId,
					pageId,
					branch: collabEntry.branch,
					sha: collabEntry.sha,
				})
			);

			if (connection) {
				const response = await connection.authority.page(connection.context);
				if (response.doc) {
					return serializePage(schema.nodeFromJSON(response.doc.toJson() as JsonObject));
				}
			}
		} catch (error) {
			logger.warn("Falling back to DocsAPI for duplicate markdown fetch", {
				sourceDocumentId,
				pageId,
				error,
			});
		}
	}

	const token = await getAccessTokenIfAvailable(get);
	return await get($docsApi).document.page.readRaw(
		{
			workspaceName: remoteEntry.workspace.name,
			documentId: sourceDocumentId,
			pageId,
			pagePath,
			ref: collabEntry?.branch ?? "main",
		},
		token
	);
};

const getRequestPageForDuplication = async ({
	get,
	sourceDocument,
	sourceDocumentId,
	sourcePages,
	page,
}: {
	get: Getter;
	sourceDocument: DocsListItem;
	sourceDocumentId: string;
	sourcePages: DocumentPageMetadata[];
	page: DocumentPageMetadata;
}): Promise<HttpRequestPage> => {
	const { pagePath, pageId } = {
		pageId: page.id,
		pagePath: page.path ?? buildDefaultPagePath(sourcePages, page, "index.yml"),
	};
	const loadedRequestPage = get(
		$httpRequestPageReader(requestPageKeyForDocsRoute({ documentId: sourceDocumentId, pageId }))
	);
	if (loadedRequestPage?.request) {
		return loadedRequestPage.request;
	}

	const remoteEntry = docsListItemRemoteEntry(sourceDocument);
	if (!remoteEntry) {
		throw new DuplicateDocumentNoSourceError(sourceDocumentId);
	}

	const collabEntry = docsListCollabEntry(remoteEntry);
	if (collabEntry) {
		try {
			const connection = await get(
				$requestPageCollabConnection({
					workspaceSlug: remoteEntry.workspace.slug,
					documentId: sourceDocumentId,
					pageId,
					branch: collabEntry.branch,
					sha: collabEntry.sha,
				})
			);
			const requestPage = connection ? getLatestRequestPage(connection.store) : null;
			if (requestPage) {
				return requestPage;
			}
		} catch (error) {
			logger.warn("Falling back to DocsAPI for duplicate request page fetch", {
				sourceDocumentId,
				pageId,
				error,
			});
		}
	}

	const token = await getAccessTokenIfAvailable(get);
	return await get($docsApi).document.page.readHttpRequest(
		{
			workspaceName: remoteEntry.workspace.name,
			documentId: sourceDocumentId,
			pageId,
			pagePath,
			ref: collabEntry?.branch ?? "main",
		},
		token
	);
};

const getCanvasSnapshotForDuplication = ({
	get,
	sourceDocumentId,
	pageId,
}: {
	get: Getter;
	sourceDocumentId: string;
	pageId: string;
}): unknown => {
	const snapshot = get(
		$pageCanvasSnapshot(canvasSnapshotKeyForDocsRoute({ documentId: sourceDocumentId, pageId }))
	);

	if (snapshot === undefined) {
		throw new DuplicateDocumentCanvasSnapshotUnavailableError(sourceDocumentId, pageId);
	}

	return snapshot;
};

const captureRemoteDocumentSnapshot = async ({
	get,
	sourceDocument,
	sourceDocumentId,
}: {
	get: Getter;
	sourceDocument: DocsListItem;
	sourceDocumentId: string;
}): Promise<DuplicateDocumentSnapshot> => {
	const metadata = await getSourceDocumentMetadataForDuplication({
		get,
		sourceDocument,
		sourceDocumentId,
	});
	const markdownPages: Record<string, string> = {};
	const requestPages: Record<string, HttpRequestPage> = {};
	const canvasSnapshots: Record<string, unknown> = {};
	const sourcePages = metadata.pagesMetadata ?? [];

	for (const page of sourcePages) {
		switch (page.type) {
			case "request":
				requestPages[page.id] = await getRequestPageForDuplication({
					get,
					sourceDocument,
					sourceDocumentId,
					sourcePages,
					page,
				});
				break;
			case "canvas": {
				const snapshot = getCanvasSnapshotForDuplication({
					get,
					sourceDocumentId,
					pageId: page.id,
				});
				canvasSnapshots[page.id] = snapshot;
				break;
			}
			case "markdown":
			case undefined:
				markdownPages[page.id] = await getMarkdownPageForDuplication({
					get,
					sourceDocument,
					sourceDocumentId,
					sourcePages,
					page,
				});
				break;
		}
	}

	return { metadata, markdownPages, requestPages, canvasSnapshots };
};

const writeDefaultTemplateOnboarding = async ({
	get,
	set,
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
}: {
	get: Getter;
	set: Setter;
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
}): Promise<void> => {
	const fsApi = fs();
	const momentDirPath = await get(fsApi.pathJoin([duplicatedDocumentDirPath, ".moment"]));
	const templateConfigPath = await get(fsApi.pathJoin([momentDirPath, "template.yml"]));
	const config = { prompts: [] };

	await set(fsApi.mkdir, { dirPath: momentDirPath });
	await set(fsApi.writeYamlFile, {
		filePath: templateConfigPath,
		content: config,
		documentId: duplicatedDocumentId,
	});
	await set($setTemplateOnboarding, { documentId: duplicatedDocumentId, config });
};

const writeFreshDocumentSupportFiles = async ({
	get,
	set,
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
}: {
	get: Getter;
	set: Setter;
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
}): Promise<void> => {
	const fsApi = fs();
	const agentsMdPath = await get(fsApi.pathJoin([duplicatedDocumentDirPath, "AGENTS.md"]));
	const claudeMdPath = await get(fsApi.pathJoin([duplicatedDocumentDirPath, "CLAUDE.md"]));

	await Promise.all([
		set(fsApi.writeTextFile, {
			filePath: agentsMdPath,
			content: AGENTS_MD_TEMPLATE,
			documentId: duplicatedDocumentId,
		}),
		set(fsApi.writeTextFile, {
			filePath: claudeMdPath,
			content: AGENTS_MD_TEMPLATE,
			documentId: duplicatedDocumentId,
		}),
	]);

	await writeDefaultTemplateOnboarding({
		get,
		set,
		duplicatedDocumentId,
		duplicatedDocumentDirPath,
	});
};

const writeRemoteDuplicateToDisk = async ({
	get,
	set,
	snapshot,
	sourceDocumentId,
	duplicatedDocumentId,
	duplicatedDocumentDirPath,
	duplicatedMetadataFilePath,
	title,
}: {
	get: Getter;
	set: Setter;
	snapshot: DuplicateDocumentSnapshot;
	sourceDocumentId: string;
	duplicatedDocumentId: string;
	duplicatedDocumentDirPath: string;
	duplicatedMetadataFilePath: string;
	title?: string;
}): Promise<DocumentMetadata> => {
	const fsApi = fs();
	const duplicatedMetadata = buildDuplicatedMetadata({
		sourceMetadata: snapshot.metadata,
		sourceDocumentId,
		duplicatedDocumentId,
		title,
	});

	await writeFreshDocumentSupportFiles({
		get,
		set,
		duplicatedDocumentId,
		duplicatedDocumentDirPath,
	});

	for (const { sourcePage, duplicatedPage } of buildDuplicatedPagePairs({
		sourceMetadata: snapshot.metadata,
		duplicatedMetadata,
	})) {
		switch (duplicatedPage.type) {
			case "request": {
				const pageFilePath = await get(
					fsApi.pathJoin([
						duplicatedDocumentDirPath,
						duplicatedPage.path ??
							buildDefaultPagePath(
								duplicatedMetadata.pagesMetadata,
								duplicatedPage,
								"index.yml"
							),
					])
				);
				const pageDirPath = await get(fsApi.dirname(pageFilePath));
				await set(fsApi.mkdir, { dirPath: pageDirPath });
				await set(fsApi.writeYamlFile, {
					filePath: pageFilePath,
					content: replaceDocumentIdInValue(
						snapshot.requestPages[sourcePage.id],
						sourceDocumentId,
						duplicatedDocumentId
					),
					documentId: duplicatedDocumentId,
				});
				break;
			}
			case "canvas": {
				const snapshotForPage = snapshot.canvasSnapshots[sourcePage.id];

				const pageDirPath = await get(
					fsApi.pathJoin([duplicatedDocumentDirPath, "pages", duplicatedPage.id])
				);
				const pageFilePath = await get(fsApi.pathJoin([pageDirPath, "index.json"]));
				await set(fsApi.mkdir, { dirPath: pageDirPath });
				await set(fsApi.writeTextFile, {
					filePath: pageFilePath,
					content: JSON.stringify(
						replaceDocumentIdInValue(
							snapshotForPage,
							sourceDocumentId,
							duplicatedDocumentId
						),
						null,
						2
					),
					documentId: duplicatedDocumentId,
				});
				break;
			}
			case "markdown":
			case undefined: {
				const pageFilePath = duplicatedPage.path
					? await get(fsApi.pathJoin([duplicatedDocumentDirPath, duplicatedPage.path]))
					: await get(
							fsApi.pathJoin([
								duplicatedDocumentDirPath,
								buildDefaultPagePath(
									duplicatedMetadata.pagesMetadata,
									duplicatedPage
								),
							])
						);
				const parentDir = await get(fsApi.dirname(pageFilePath));
				await set(fsApi.mkdir, { dirPath: parentDir });
				await set(fsApi.writeTextFile, {
					filePath: pageFilePath,
					content: replaceDocumentIdInString(
						snapshot.markdownPages[sourcePage.id] ?? "",
						sourceDocumentId,
						duplicatedDocumentId
					),
					documentId: duplicatedDocumentId,
				});
				break;
			}
		}
	}

	await set(fsApi.writeYamlFile, {
		filePath: duplicatedMetadataFilePath,
		content: duplicatedMetadata,
		documentId: duplicatedDocumentId,
	});

	return duplicatedMetadata;
};

export type DuplicateDocumentInput = {
	sourceDocumentId: string;
	documentId?: string;
	title?: string;
};

export const $duplicateDocumentWorkflow = atom(
	null,
	async (get, set, input: DuplicateDocumentInput) => {
		if (!env.isDesktopApp) {
			throw new Error("Filesystem not available");
		}

		const sourceDocument = get($document(input.sourceDocumentId));
		if (!sourceDocument) {
			throw new DuplicateDocumentNotFoundError(input.sourceDocumentId);
		}

		const sourceLocalEntry = Object.values(sourceDocument.local)[0];
		const sourceRemoteEntry = docsListItemRemoteEntry(sourceDocument);
		if (!sourceLocalEntry && !sourceRemoteEntry) {
			throw new DuplicateDocumentNoSourceError(input.sourceDocumentId);
		}

		const duplicatedDocumentId = input.documentId ?? generateShortUuid();
		const sourceDocumentDirPath = sourceLocalEntry?.path;

		const fsApi = fs();
		const git = get($git);
		const pathsApi = paths();

		const duplicatedDocumentDirPath = await get(
			pathsApi.getDocumentDirPath(duplicatedDocumentId)
		);
		const duplicatedMetadataFilePath = await get(
			pathsApi.getDocumentMetadataFilePath(duplicatedDocumentId)
		);
		const duplicatedDocumentEnvironmentsDirPath = await get(
			pathsApi.getDocumentEnvironmentsDirPath(duplicatedDocumentId)
		);

		logger.debug("Starting duplicate document workflow", {
			sourceDocumentId: input.sourceDocumentId,
			duplicatedDocumentId,
			sourceDocumentDirPath,
			sourceRemoteWorkspace: sourceRemoteEntry?.workspace.name,
			duplicatedDocumentDirPath,
		});

		try {
			let sourceMetadata: DocumentMetadata;
			let duplicatedMetadata: DocumentMetadata;

			if (sourceLocalEntry && sourceDocumentDirPath) {
				const sourceMetadataFilePath = await get(
					pathsApi.getDocumentMetadataFilePath(input.sourceDocumentId)
				);
				const sourceMetadataRaw = await get(
					fsApi.readYamlFile({ filePath: sourceMetadataFilePath })
				);
				const sourceMetadataResult = DocumentMetadataSchema.safeParse(sourceMetadataRaw);
				if (!sourceMetadataResult.success) {
					throw sourceMetadataResult.error;
				}

				sourceMetadata = sourceMetadataResult.data;

				await copyDirectoryTree({
					get,
					set,
					sourceDirPath: sourceDocumentDirPath,
					destinationDirPath: duplicatedDocumentDirPath,
				});
				await duplicateDocumentEnvironments({
					get,
					set,
					sourceDocumentId: input.sourceDocumentId,
					duplicatedDocumentId,
				});
				await replaceDocumentIdInTextFiles({
					get,
					set,
					rootDirPath: duplicatedDocumentDirPath,
					sourceDocumentId: input.sourceDocumentId,
					duplicatedDocumentId,
					skipDirNames: INTERNAL_REPO_DIR_NAMES,
				});

				const duplicatedEnvironmentsDirExists = await get(
					fsApi.exists({ filePath: duplicatedDocumentEnvironmentsDirPath })
				);
				if (duplicatedEnvironmentsDirExists) {
					await replaceDocumentIdInTextFiles({
						get,
						set,
						rootDirPath: duplicatedDocumentEnvironmentsDirPath,
						sourceDocumentId: input.sourceDocumentId,
						duplicatedDocumentId,
					});
				}

				duplicatedMetadata = buildDuplicatedMetadata({
					sourceMetadata,
					sourceDocumentId: input.sourceDocumentId,
					duplicatedDocumentId,
					title: input.title,
				});

				await set(fsApi.writeYamlFile, {
					filePath: duplicatedMetadataFilePath,
					content: duplicatedMetadata,
					documentId: duplicatedDocumentId,
				});

				await removeRemoteEntriesFromGitConfig({
					get,
					set,
					duplicatedDocumentId,
					duplicatedDocumentDirPath,
				});
			} else {
				const sourceSnapshot = await captureRemoteDocumentSnapshot({
					get,
					sourceDocument,
					sourceDocumentId: input.sourceDocumentId,
				});

				sourceMetadata = sourceSnapshot.metadata;
				await git.init({ path: duplicatedDocumentDirPath });
				duplicatedMetadata = await writeRemoteDuplicateToDisk({
					get,
					set,
					snapshot: sourceSnapshot,
					sourceDocumentId: input.sourceDocumentId,
					duplicatedDocumentId,
					duplicatedDocumentDirPath,
					duplicatedMetadataFilePath,
					title: input.title,
				});
			}

			await git.commitAndRefresh({
				path: duplicatedDocumentDirPath,
				message: getDuplicateCommitMessage(sourceMetadata.title),
			});

			const duplicatedDocsListItem = buildDuplicatedDocsListItem({
				duplicatedDocumentId,
				duplicatedDocumentDirPath,
				duplicatedMetadata,
			});

			set($addDraftDocument, duplicatedDocsListItem);
			set($loadDocumentMetadataFromDisk, {
				key: metadataKeyForDocsRoute(duplicatedDocumentId),
				document: duplicatedDocsListItem,
			});

			if (sourceLocalEntry) {
				await loadTemplateOnboardingIfPresent({
					get,
					set,
					duplicatedDocumentId,
					duplicatedDocumentDirPath,
				});
			}

			logger.info("Duplicate document workflow completed", {
				sourceDocumentId: input.sourceDocumentId,
				duplicatedDocumentId,
			});

			return duplicatedDocsListItem;
		} catch (error) {
			logger.error("Duplicate document workflow failed", {
				sourceDocumentId: input.sourceDocumentId,
				duplicatedDocumentId,
				error,
			});
			await cleanupDuplicatedPaths({
				set,
				documentDirPath: duplicatedDocumentDirPath,
				documentEnvironmentsDirPath: duplicatedDocumentEnvironmentsDirPath,
			});
			throw error;
		}
	}
);
$duplicateDocumentWorkflow.debugLabel = "duplicateDocumentWorkflow";
